<template>
    <div class="row claims-report-frame">
        <div class="col s12 claims-report-frame-col">
            <LoadingDiv v-if="!isReady" />

            <div class="claims-report-shell sba-slide-in" ref="reportShell" v-else>
                <form @submit.prevent="handleSubmit">
                    <div class="claims-report-head">
                        <label class="active teal-text">Claims Report</label>
                        <h5>Download Claims Report</h5>
                        <p>Choose the scheme, report scope, and period you want to download.</p>
                    </div>

                    <div class="report-form-panel">
                        <div class="input-field">
                            <select v-model="scheme">
                                <option :value="scheme.id" v-for="scheme in schemes" :key="scheme.id">
                                    {{ scheme.name }}
                                </option>
                            </select>
                            <label>Scheme</label>
                        </div>
                    </div>

                    <div class="report-form-panel" v-if="scheme">
                        <div class="input-field">
                            <select v-model="scope">
                                <option value="claim">
                                    By Claims
                                </option>
                                <option value="pay">
                                    By Payment
                                </option>
                            </select>
                            <label>Report Scope</label>
                        </div>
                    </div>

                    <div class="report-form-panel" v-if="scheme && scope">
                        <div class="input-field" @click="periodStart = null">
                            <select v-model="periodStart">
                                <option :value="thisMonthDate">
                                    {{ thisMonth }} (Current month)
                                </option>
                                <option :value="pastMonth.date" v-for="pastMonth in pastInactiveMonths"
                                    :key="pastMonth.date">
                                    {{ pastMonth.month }}
                                </option>
                            </select>
                            <label>Report Period</label>
                        </div>
                    </div>


                    <div class="report-submit-strip">
                        <button class="waves-effect waves-light btn-large" :class="{
                            disabled:
                                !scheme || !scope || !periodStart || isSubmitting,
                        }">
                            <i class="material-icons right">download</i>{{ isSubmitting ? `Downloading` : "Download"
                            }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

</template>

<script>
import { onBeforeUnmount, onMounted, onUpdated, ref } from "vue";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import {
    activeMonths,
    pastMonths,
} from "@/composables/fxLibrary/months";
import LoadingDiv from "@/components/LoadingDiv.vue";
import {
    toMonthYearFx,
    capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import {
    grossTotalSentFx,
    grossTotalApprovedFx,
    batchTotalSentFx,
    batchTotalApprovedFx,
    paymentStatusFx,
} from "@/composables/calFunction/cal";
import {
    downloadExcelFx,
} from "@/composables/reportFunction/downloader";
import M from "materialize-css";

export default {
    name: "ClaimsInsightComponent",
    components: { LoadingDiv },
    setup() {
        const isReady = ref(true);
        const error = ref(null);
        const isSubmitting = ref(false);
        const scheme = ref(null);
        const scope = ref(null);
        const thisMonth = ref(null);
        const thisMonthDate = ref(null);
        const pastInactiveMonths = ref([]);
        const periodStart = ref(null);
        const reportShell = ref(null);

        // store
        const store = useStore();

        const forceDropdownDown = (instance) => {
            if (!instance?.dropdown || instance.dropdown._easyHmoForceDown) {
                return;
            }

            const dropdown = instance.dropdown;
            const getDropdownPosition = dropdown._getDropdownPosition.bind(dropdown);

            dropdown._getDropdownPosition = function () {
                const positionInfo = getDropdownPosition();
                const triggerBounds = this.el.getBoundingClientRect();
                const dropdownBounds = this.dropdownEl.getBoundingClientRect();
                const availableHeight = window.innerHeight - triggerBounds.bottom - 16;

                return {
                    ...positionInfo,
                    y: triggerBounds.top - dropdownBounds.top + triggerBounds.height,
                    verticalAlignment: "top",
                    height: Math.min(positionInfo.height, Math.max(120, availableHeight)),
                };
            };

            dropdown._easyHmoForceDown = true;
        };

        const destroyReportSelects = () => {
            if (!reportShell.value) {
                return;
            }

            reportShell.value.querySelectorAll("select").forEach((select) => {
                const instance = M.FormSelect.getInstance(select);

                if (instance) {
                    instance.destroy();
                }
            });
        };

        const initializeReportSelects = () => {
            if (!reportShell.value) {
                return;
            }

            destroyReportSelects();

            const elemsSelect = reportShell.value.querySelectorAll("select");
            M.FormSelect.init(elemsSelect, {
                dropdownOptions: {
                    container: document.body,
                    coverTrigger: false,
                    constrainWidth: true,
                },
            });

            elemsSelect.forEach((select) => {
                const instance = M.FormSelect.getInstance(select);

                if (instance?.dropdownOptions) {
                    instance.dropdownOptions.classList.add("claims-report-select-dropdown");
                }

                forceDropdownDown(instance);
            });
        };

        // select
        onMounted(() => {
            initializeReportSelects();
        });

        // select
        onUpdated(() => {
            initializeReportSelects();
        });

        onBeforeUnmount(() => {
            destroyReportSelects();
        });

        // payment status
        const paymentStatus = (cluster) => {
            return paymentStatusFx(cluster);
        }
        // batch total approved
        const batchTotalApproved = (clusters) => {
            let batch = {};
            batch.clusters = clusters;
            return batchTotalApprovedFx(batch);
        };

        // batch total sent
        const batchTotalSent = (clusters) => {
            let batch = {};
            batch.clusters = clusters;
            return batchTotalSentFx(batch);
        };

        // total calculations (gross sent)
        const grossTotalSent = (encounters) => {
            return grossTotalSentFx(encounters);
        };

        // total calculations (gross approved)
        const grossTotalApproved = (encounters, db) => {
            return grossTotalApprovedFx(encounters, db);
        };

        //from functions
        const capWords = (val) => {
            return capWordsFx(val);
        };

        // convert to Month & Year
        const toMonthYear = (val) => {
            return toMonthYearFx(val);
        };

        // month initialization
        onMounted(() => {
            // active
            const { fx: active } = activeMonths();
            // active months
            thisMonth.value = active.value.thisMonth;
            thisMonthDate.value = active.value.thisMonthDate;

            // past
            const { fx: past } = pastMonths();
            // past month array
            pastInactiveMonths.value = past.value;
        });

        // clear
        const clearForm = () => {
            isReady.value = true;
            isSubmitting.value = false;
            periodStart.value = null;
            scheme.value = null;
            scope.value = null;
        };

        // // handle submit
        // const handleSubmit = async () => {
        //     if (scheme.value || scope.value || periodStart.value
        //     ) {
        //         isReady.value = false;

        //         let period;
        //         let searchParams;

        //         if (scope.value === 'claim') {
        //             period = periodStart.value.slice(5, 7).replace(/^0/, '') + '_' + periodStart.value.slice(0, 4);
        //         } else {
        //             period = periodStart.value // unchanged
        //         }

        //         // get records
        //         searchParams = `?s=${scheme.value}&t=${scope.value}&p=${period}`;
        //         const { res, err } = await getItems(`operator/insight/claims${searchParams}`);

        //         if (res.value) {

        //             let paid = []; // hmo
        //             let complete = []; // gov
        //             let pending = [];

        //             res.value.data.forEach((x) => {
        //                 if (x.payment && x.payment?.stage === 'c') {
        //                     paid.push({ ...x });
        //                 } else if (x.payment && x.payment?.stage === 'z') {
        //                     complete.push({ ...x });
        //                 } else {
        //                     pending.push({ ...x });
        //                 }

        //             });


        //             // download
        //             let data = [];

        //             // check complete
        //             if (complete.length) {
        //                 // top
        //                 complete.forEach((x, y) => {

        //                     // all
        //                     data.push({
        //                         No: y + 1,
        //                         Provider: capWords(x.provider.name),
        //                         Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
        //                         Sent: grossTotalSent(x.encounters),
        //                         Approved: grossTotalApproved(x.encounters),
        //                         Comment: paymentStatus(x),
        //                     })

        //                 })

        //                 // space
        //                 data.push({})

        //                 // final paid
        //                 data.push({
        //                     No: "",
        //                     Provider: "",
        //                     Period: "Total",
        //                     Sent: batchTotalSent(complete),
        //                     Approved: batchTotalApproved(complete),
        //                     Comment: "",
        //                 })

        //                 // space
        //                 data.push({})
        //                 // space
        //                 data.push({})

        //             }

        //             // check paid
        //             if (paid.length) {
        //                 // top
        //                 paid.forEach((x, y) => {

        //                     // all
        //                     data.push({
        //                         No: y + 1,
        //                         Provider: capWords(x.provider.name),
        //                         Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
        //                         Sent: grossTotalSent(x.encounters),
        //                         Approved: grossTotalApproved(x.encounters),
        //                         Comment: paymentStatus(x),
        //                     })

        //                 })

        //                 // space
        //                 data.push({})

        //                 // final paid
        //                 data.push({
        //                     No: "",
        //                     Provider: "",
        //                     Period: "Total",
        //                     Sent: batchTotalSent(paid),
        //                     Approved: batchTotalApproved(paid),
        //                     Comment: "",
        //                 })

        //                 // space
        //                 data.push({})
        //                 // space
        //                 data.push({})

        //             }


        //             // check pending
        //             if (pending.length) {
        //                 // top
        //                 pending.forEach((x, y) => {

        //                     // all
        //                     data.push({
        //                         No: y + 1,
        //                         Provider: capWords(x.provider.name),
        //                         Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
        //                         Sent: grossTotalSent(x.encounters),
        //                         Approved: x.status === 'v' ? grossTotalApproved(x.encounters) : 0,
        //                         Comment: paymentStatus(x),
        //                     })

        //                 })

        //                 // space
        //                 data.push({})

        //                 // final unpaid
        //                 data.push({
        //                     No: "",
        //                     Provider: "",
        //                     Period: "Total",
        //                     Sent: batchTotalSent(pending),
        //                     Approved: "", // keep empty for pending
        //                     Comment: "",
        //                 })

        //                 // space
        //                 data.push({})
        //                 // space
        //                 data.push({})

        //             }


        //             if (data.length) {
        //                 let schemeFullName;
        //                 let description;
        //                 let month = periodStart.value

        //                 schemeFullName = store.state.user.schemes.find(x => scheme.value === x.id).name.toUpperCase();
        //                 if (scope.value === "claim") {
        //                     description = "By_Submission";
        //                 } else {
        //                     description = "By_Payment_Only";
        //                 }

        //                 // end 
        //                 clearForm();

        //                 // notify (Toast)
        //                 M.toast({
        //                     html: "Utilization Report Downloading <i class='material-icons'>check_circle</i>", // Toast content
        //                     // classes: 'green rounded', // Add custom classes
        //                     displayLength: 5000, // Duration in milliseconds
        //                     inDuration: 300, // Transition in duration
        //                     outDuration: 300, // Transition out duration
        //                 });

        //                 // fire function
        //                 return downloadExcelFx(data, `Claims_Report_${schemeFullName}_${month}_${description}`);

        //             }

        //             // notify (Toast)
        //             M.toast({
        //                 html: "No Report Available!", // Toast content
        //                 // classes: 'green rounded', // Add custom classes
        //                 displayLength: 5000, // Duration in milliseconds
        //                 inDuration: 300, // Transition in duration
        //                 outDuration: 300, // Transition out duration
        //             });

        //             // end 
        //             clearForm();

        //         } else {
        //             // error set    
        //             error.value = err.value;
        //             // end 
        //             clearForm();
        //         }

        //     } else {
        //         // error
        //         clearForm();
        //     }
        // };

        // handle submit
        const handleSubmit = async () => {

            if (scope.value === 'claim') {

                if (scheme.value || scope.value || periodStart.value
                ) {
                    isReady.value = false;

                    let period;
                    let searchParams;

                    if (scope.value === 'claim') {
                        period = periodStart.value.slice(5, 7).replace(/^0/, '') + '_' + periodStart.value.slice(0, 4);
                    } else {
                        period = periodStart.value // unchanged
                    }

                    // get records
                    searchParams = `?s=${scheme.value}&t=${scope.value}&p=${period}`;
                    const { res, err } = await getItems(`operator/insight/claims${searchParams}`);

                    if (res.value) {

                        let all = []; // gov

                        res.value.data.forEach((x) => {
                            all.push({ ...x });
                        });


                        // download
                        let data = [];

                        // check all
                        if (all.length) {
                            // top
                            all.forEach((x, y) => {

                                // all
                                data.push({
                                    No: y + 1,
                                    Provider: capWords(x.provider.name),
                                    Client: "",
                                    Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
                                    Enrollee: x.encounters[0]?.name,
                                    ID: "",
                                    Encounter: "",
                                    Diagnosis: x.encounters[0]?.diagnosis,
                                    Claims: `${x.encounters[0]?.claims[0]?.item} (x ${x.encounters[0]?.claims[0]?.sent_qty})`,
                                    Sent: grossTotalSent(x.encounters),
                                    Approved: grossTotalApproved(x.encounters),
                                    Comment: paymentStatus(x),
                                })

                                x.encounters.forEach((encounter, j) => {
                                    if (j > 0) {

                                        data.push({
                                            No: "",
                                            Provider: "",
                                            Client: "",
                                            Period: "",
                                            Enrollee: encounter.name,
                                            ID: "",
                                            Encounter: "",
                                            Diagnosis: encounter.diagnosis,
                                            Claims: `${x.encounters[1]?.claims[1]?.item} (x ${x.encounters[1]?.claims[1]?.sent_qty})`,
                                            Sent: "",
                                            Approved: "",
                                            Comment: "",
                                        })

                                    }

                                    encounter.claims.forEach((claim, k) => {

                                        if (k > 1) {

                                            data.push({
                                                No: "",
                                                Provider: "",
                                                Client: "",
                                                Period: "",
                                                Enrollee: "",
                                                ID: "",
                                                Encounter: "",
                                                Diagnosis: "",
                                                Claims: `${claim.item} (x ${claim.sent_qty})`,
                                                Sent: "",
                                                Approved: "",
                                                Comment: "",
                                            })

                                        }

                                    })
 
                            // space
                            data.push({})

                                })

                                
                            // space
                            data.push({})

                            })

                            // space
                            data.push({})

                            // final paid
                            data.push({
                                No: "",
                                Provider: "",
                                Client: "",
                                Period: "Total",
                                Enrollee: "",
                                ID: "",
                                Encounter: "",
                                Diagnosis: "",
                                Claims: "",
                                Sent: batchTotalSent(all),
                                Approved: batchTotalApproved(all),
                                Comment: "",
                            })

                            // space
                            data.push({})
                            // space
                            data.push({})

                        }


                        if (data.length) {
                            let schemeFullName;
                            let description;
                            let month = periodStart.value

                            schemeFullName = store.state.user.schemes.find(x => scheme.value === x.id).name.toUpperCase();
                            if (scope.value === "claim") {
                                description = "By_Submission";
                            } else {
                                description = "By_Payment_Only";
                            }

                            // end 
                            clearForm();

                            // notify (Toast)
                            M.toast({
                                html: "Utilization Report Downloading <i class='material-icons'>check_circle</i>", // Toast content
                                // classes: 'green rounded', // Add custom classes
                                displayLength: 5000, // Duration in milliseconds
                                inDuration: 300, // Transition in duration
                                outDuration: 300, // Transition out duration
                            });

                            // fire function
                            return downloadExcelFx(data, `Claims_Report_${schemeFullName}_${month}_${description}`);

                        }

                        // notify (Toast)
                        M.toast({
                            html: "No Report Available!", // Toast content
                            // classes: 'green rounded', // Add custom classes
                            displayLength: 5000, // Duration in milliseconds
                            inDuration: 300, // Transition in duration
                            outDuration: 300, // Transition out duration
                        });

                        // end 
                        clearForm();

                    } else {
                        // error set    
                        error.value = err.value;
                        // end 
                        clearForm();
                    }

                } else {
                    // error
                    clearForm();
                }

            // payments
            } else {

                if (scheme.value || scope.value || periodStart.value
                ) {
                    isReady.value = false;

                    let period;
                    let searchParams;

                    if (scope.value === 'claim') {
                        period = periodStart.value.slice(5, 7).replace(/^0/, '') + '_' + periodStart.value.slice(0, 4);
                    } else {
                        period = periodStart.value // unchanged
                    }

                    // get records
                    searchParams = `?s=${scheme.value}&t=${scope.value}&p=${period}`;
                    const { res, err } = await getItems(`operator/insight/claims${searchParams}`);

                    if (res.value) {

                        let paid = []; // hmo
                        let complete = []; // gov
                        let pending = [];

                        res.value.data.forEach((x) => {
                            if (x.payment && x.payment?.stage === 'c') {
                                paid.push({ ...x });
                            } else if (x.payment && x.payment?.stage === 'z') {
                                complete.push({ ...x });
                            } else {
                                pending.push({ ...x });
                            }

                        });


                        // download
                        let data = [];

                        // check complete
                        if (complete.length) {
                            // top
                            complete.forEach((x, y) => {

                                // all
                                data.push({
                                    No: y + 1,
                                    Provider: capWords(x.provider.name),
                                    Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
                                    Sent: grossTotalSent(x.encounters),
                                    Approved: grossTotalApproved(x.encounters),
                                    Comment: paymentStatus(x),
                                })

                            })

                            // space
                            data.push({})

                            // final paid
                            data.push({
                                No: "",
                                Provider: "",
                                Period: "Total",
                                Sent: batchTotalSent(complete),
                                Approved: batchTotalApproved(complete),
                                Comment: "",
                            })

                            // space
                            data.push({})
                            // space
                            data.push({})

                        }

                        // check paid
                        if (paid.length) {
                            // top
                            paid.forEach((x, y) => {

                                // all
                                data.push({
                                    No: y + 1,
                                    Provider: capWords(x.provider.name),
                                    Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
                                    Sent: grossTotalSent(x.encounters),
                                    Approved: grossTotalApproved(x.encounters),
                                    Comment: paymentStatus(x),
                                })

                            })

                            // space
                            data.push({})

                            // final paid
                            data.push({
                                No: "",
                                Provider: "",
                                Period: "Total",
                                Sent: batchTotalSent(paid),
                                Approved: batchTotalApproved(paid),
                                Comment: "",
                            })

                            // space
                            data.push({})
                            // space
                            data.push({})

                        }


                        // check pending
                        if (pending.length) {
                            // top
                            pending.forEach((x, y) => {

                                // all
                                data.push({
                                    No: y + 1,
                                    Provider: capWords(x.provider.name),
                                    Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
                                    Sent: grossTotalSent(x.encounters),
                                    Approved: x.status === 'v' ? grossTotalApproved(x.encounters) : 0,
                                    Comment: paymentStatus(x),
                                })

                            })

                            // space
                            data.push({})

                            // final unpaid
                            data.push({
                                No: "",
                                Provider: "",
                                Period: "Total",
                                Sent: batchTotalSent(pending),
                                Approved: "", // keep empty for pending
                                Comment: "",
                            })

                            // space
                            data.push({})
                            // space
                            data.push({})

                        }


                        if (data.length) {
                            let schemeFullName;
                            let description;
                            let month = periodStart.value

                            schemeFullName = store.state.user.schemes.find(x => scheme.value === x.id).name.toUpperCase();
                            if (scope.value === "claim") {
                                description = "By_Submission";
                            } else {
                                description = "By_Payment_Only";
                            }

                            // end 
                            clearForm();

                            // notify (Toast)
                            M.toast({
                                html: "Payment Report Downloading <i class='material-icons'>check_circle</i>", // Toast content
                                // classes: 'green rounded', // Add custom classes
                                displayLength: 5000, // Duration in milliseconds
                                inDuration: 300, // Transition in duration
                                outDuration: 300, // Transition out duration
                            });

                            // fire function
                            return downloadExcelFx(data, `Claims_Report_${schemeFullName}_${month}_${description}`);

                        }

                        // notify (Toast)
                        M.toast({
                            html: "No Report Available!", // Toast content
                            // classes: 'green rounded', // Add custom classes
                            displayLength: 5000, // Duration in milliseconds
                            inDuration: 300, // Transition in duration
                            outDuration: 300, // Transition out duration
                        });

                        // end 
                        clearForm();

                    } else {
                        // error set    
                        error.value = err.value;
                        // end 
                        clearForm();
                    }

                } else {
                    // error
                    clearForm();
                }

            }
        };

        return {
            isReady,
            isSubmitting,
            handleSubmit,
            error,
            scheme,
            scope,
            thisMonth,
            thisMonthDate,
            pastInactiveMonths,
            periodStart,
            reportShell,
            schemes: store.state.user.schemes,
        };
    },
};
</script>

<style scoped>
.claims-report-frame {
    margin-left: 0;
    margin-right: 0;
}

.claims-report-frame-col {
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.claims-report-shell {
    color: #263238;
}

.claims-report-head,
.report-form-panel,
.report-submit-strip {
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    box-shadow: none;
}

.claims-report-head {
    background: #ffffff;
    border-color: #f0f0f0;
    margin-bottom: 14px;
    overflow: visible;
    padding: 24px 26px;
    position: relative;
    z-index: 2;
}

.claims-report-head::before {
    border: 1px solid #c4d3d9;
    border-bottom: 0;
    border-radius: 7px 7px 0 0;
    content: "";
    height: 18px;
    left: -1px;
    pointer-events: none;
    position: absolute;
    right: -1px;
    top: -1px;
    z-index: 1;
}

.claims-report-head > * {
    position: relative;
    z-index: 2;
}

.claims-report-head h5 {
    color: #263238;
    font-weight: 700;
    margin: 4px 0 0;
}

.claims-report-head p {
    color: #607d8b;
    line-height: 1.55;
    margin: 8px 0 0;
    max-width: 780px;
}

.report-form-panel {
    margin-bottom: 14px;
    padding: 8px 18px 4px;
}

.report-submit-strip {
    background: #f5f5f5;
    margin-top: 16px;
    padding: 16px;
}

.report-submit-strip button {
    min-width: 190px;
}

:global(.claims-report-select-dropdown) {
    z-index: 1600;
}
</style>
